import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Quelle est la durée de mise en place de l\'Usine à Leads ?',
      answer: 'La mise en place complète prend entre 4 et 8 semaines selon le périmètre et la complexité de votre projet. Nous travaillons en mode agile pour vous livrer rapidement des résultats concrets. Les premiers leads commencent à arriver dès les premières semaines de déploiement.'
    },
    {
      question: 'Y a-t-il un engagement de durée ?',
      answer: 'Non, il n\'y a aucun engagement de durée sur l\'abonnement mensuel. Vous pouvez résilier à tout moment si vous n\'êtes pas satisfait. Cependant, nous recommandons un minimum de 3 mois pour voir les résultats optimaux et permettre aux optimisations de porter leurs fruits.'
    },
    {
      question: 'Quels types de résultats puis-je attendre ?',
      answer: 'Nos clients constatent en moyenne une augmentation de 47% de leurs leads qualifiés dans les 90 premiers jours. Les résultats varient selon votre secteur, votre marché et votre investissement publicitaire, mais nous garantissons une amélioration mesurable de vos performances digitales.'
    },
    {
      question: 'Comment fonctionnent les automatisations ?',
      answer: 'Nous mettons en place des relances automatiques via WhatsApp et email pour ne perdre aucun prospect. Chaque lead reçoit un suivi personnalisé selon son comportement, avec des messages adaptés à chaque étape de son parcours. Tout est automatisé pour maximiser vos conversions sans effort manuel.'
    },
    {
      question: 'Qui gère les campagnes publicitaires ?',
      answer: 'Notre équipe d\'experts gère l\'intégralité de vos campagnes publicitaires : création, optimisation, suivi et reporting. Vous gardez le contrôle total via des tableaux de bord en temps réel et des points réguliers avec votre chef de projet dédié.'
    },
    {
      question: 'Que se passe-t-il si je ne suis pas satisfait ?',
      answer: 'Si vous n\'êtes pas satisfait des résultats ou de notre accompagnement, nous vous offrons 3 mois d\'optimisation gratuits supplémentaires pour maximiser vos performances. Notre objectif est votre réussite, et nous mettons tout en œuvre pour y parvenir.'
    },
    {
      question: 'Quel budget publicitaire dois-je prévoir en plus ?',
      answer: 'Le budget publicitaire dépend de vos objectifs et de votre marché. Nous recommandons généralement un budget minimum de 500 € à 2 000 € par mois pour obtenir des résultats significatifs. Nous vous aidons à définir le budget optimal lors de l\'audit initial.'
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden" itemScope itemType="https://schema.org/FAQPage">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Questions fréquentes sur la <strong>Génération de Leads</strong>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
            Tout ce que vous devez savoir sur l'Usine à Leads
          </p>
        </header>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <article
              key={index}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <h3 itemProp="name">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-3 sm:gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-question-line text-lg sm:text-xl text-white" aria-hidden="true"></i>
                    </div>
                    <span className="text-base sm:text-lg font-bold text-gray-900 flex-1 break-words">
                      {faq.question}
                    </span>
                  </div>
                  <div className={`w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-xl text-orange-600" aria-hidden="true"></i>
                  </div>
                </button>
              </h3>
              
              <div
                id={`faq-answer-${index}`}
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="px-6 sm:px-8 pb-5 sm:pb-6 pl-14 sm:pl-20">
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base break-words" itemProp="text">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 sm:mt-16 text-center bg-gradient-to-r from-orange-50 to-teal-50 rounded-2xl p-6 sm:p-8 border border-orange-200">
          <i className="ri-customer-service-line text-3xl sm:text-4xl text-orange-600 mb-4" aria-hidden="true"></i>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 break-words">
            Vous avez d'autres questions ?
          </h3>
          <p className="text-gray-600 mb-6 text-sm sm:text-base break-words">
            Notre équipe est là pour répondre à toutes vos interrogations
          </p>
          <a
            href="#contact-form"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            aria-label="Contactez-nous pour plus d'informations"
          >
            Contactez-nous
          </a>
        </div>
      </div>
    </section>
  );
}
